import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Crosshair, X, Loader2, RefreshCw, Activity, Building2 } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useAccountStore } from '../../stores/accountStore';
import { useMarketStore } from '../../stores/marketStore';
import { usePositionPnl } from '../../hooks/ui/usePositionPnl';
import { useUserKalshiPOS } from '../../hooks/useUserKalshiPOS';
import { ActivityTicker } from '../../components/ActivityTicker';
import { OrgTradersTable, type OrgTraderRow } from '../../components/organisms/OrgTradersTable';
import { describeMarket, truncateTicker } from '../../lib/kalshiTicker';
import { playQueueWarningTone, playQueueCriticalTone } from '../../lib/audio/tradeAudio';
import { readSoundSettings } from '../../lib/audio/soundSettings';

// Queue-position alert thresholds (lower = closer to the front of the
// resting-bid queue). Crossing each threshold from above fires a one-shot
// audio cue; the row stays tinted while the order remains at or below the
// threshold so the visual matches the audible state.
const QUEUE_WARNING_THRESHOLD = 50_000;
const QUEUE_CRITICAL_THRESHOLD = 10_000;

// ── Helpers ─────────────────────────────────────────────────────

function formatCents(cents: number): string {
  return `${cents}¢`;
}

function formatDollars(cents: number): string {
  const abs = Math.abs(cents);
  const sign = cents >= 0 ? '+' : '-';
  return `${sign}$${(abs / 100).toFixed(2)}`;
}

function formatDollarsSigned(cents: number): string {
  const sign = cents >= 0 ? '+' : '-';
  return `${sign}$${(Math.abs(cents) / 100).toFixed(2)}`;
}

// ── Public component (role dispatcher) ──────────────────────────

interface PositionTrackerProps {
  onSelectTrader?: (row: OrgTraderRow) => void;
  onSelectOrg?: (row: OrgSummaryRow) => void;
}

export function PositionTracker({ onSelectTrader }: PositionTrackerProps = {}) {
  const role = useAuthStore((s) => s.user?.role ?? null);
  const userId = useAuthStore((s) => s.user?.id ?? null);

  if (role === 'manager') {
    return (
      <ManagerPositionsView
        currentUserId={userId}
        onSelectTrader={onSelectTrader}
      />
    );
  }
  return <TraderView />;
}

// ── Trader view (own data only) ─────────────────────────────────

function TraderView() {
  const status = useAccountStore((s) => s.status);
  const balance = useAccountStore((s) => s.balance);
  const positions = useAccountStore((s) => s.positions);
  const openOrders = useAccountStore((s) => s.openOrders);
  const keyMode = useAccountStore((s) => s.keyMode);
  const realizedPnlCents = useAccountStore((s) => s.realizedPnlCents);
  const cancelOrder = useAccountStore((s) => s.cancelOrder);
  const refreshPositions = useAccountStore((s) => s.refreshPositions);
  const refreshOrders = useAccountStore((s) => s.refreshOrders);
  const refreshPnl = useAccountStore((s) => s.refreshPnl);
  const placeOrder = useAccountStore((s) => s.placeOrder);

  const { positionsWithPnl, totalPnl } = usePositionPnl();

  // Last-trade price (hydra-sourced) for the Open Orders "Last" column.
  const getLastTradeYesCents = useMarketStore((s) => s.getLastTradeYesCents);
  // Hydra orderbook for cumulative queue depth (same-side levels at strictly
  // better prices fill before this order does, so they belong in "queue").
  const getOrderbookRaw = useMarketStore((s) => s.getOrderbookRaw);
  // Subscribe to version so book updates trigger re-derivation of the
  // cumulative queue map (the accessors void-touch version internally for
  // tracking, but the component still needs an explicit subscription).
  const marketVersion = useMarketStore((s) => s.version);

  // Queue position lives on /api/kalshi/me/snapshot (the relay's per-user
  // shape) — it isn't carried on accountStore.openOrders. Pull it via the
  // existing snapshot hook and key by orderId.
  const orderTickers = useMemo(
    () => Array.from(new Set(openOrders.map((o) => o.ticker))),
    [openOrders],
  );
  const { ordersByTicker } = useUserKalshiPOS(orderTickers);

  // Tell hydra to stream orderbook frames for the user's open-order tickers —
  // the cumulative queue calc needs the live book, and PositionTracker may
  // be opened standalone (without ValueDashboard already subscribing). Use a
  // stable key so the effect only fires when the ticker set actually changes.
  const orderTickersKey = useMemo(() => orderTickers.slice().sort().join('|'), [orderTickers]);
  useEffect(() => {
    useMarketStore.getState().setOrderbookNeeds('position-tracker', orderTickers);
    return () => {
      useMarketStore.getState().setOrderbookNeeds('position-tracker', []);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderTickersKey]);

  // Cumulative queue depth = at-level (Kalshi's queue_position_fp) + size of
  // all same-side resting bids at strictly better prices, since those fill
  // before this order does. Falls back to the at-level value when the book
  // hasn't loaded yet (a known lower bound — better to under-report than to
  // hide the row entirely while we wait for the WS feed).
  const cumulativeQueueByOrderId = useMemo(() => {
    void marketVersion;
    const map = new Map<string, number | null>();
    for (const order of openOrders) {
      const slices = ordersByTicker.get(order.ticker) ?? [];
      const slice = slices.find((s) => s.orderId === order.orderId);
      const atLevel = slice?.queuePosition ?? null;
      if (atLevel == null) {
        map.set(order.orderId, null);
        continue;
      }
      const book = getOrderbookRaw(order.ticker);
      if (!book) {
        map.set(order.orderId, atLevel);
        continue;
      }
      const sideLevels = order.side === 'yes' ? book.yes : book.no;
      // The order's own bid price — needed to identify "strictly better"
      // same-side levels. Mirrors the Bid column's yes_price-only fallback.
      const bidCents =
        order.side === 'yes'
          ? (order.yesPrice ?? (order.noPrice != null ? 100 - order.noPrice : null))
          : (order.noPrice ?? (order.yesPrice != null ? 100 - order.yesPrice : null));
      if (bidCents == null) {
        map.set(order.orderId, atLevel);
        continue;
      }
      let better = 0;
      for (const lvl of sideLevels) {
        if (lvl.price > bidCents) better += lvl.size;
      }
      map.set(order.orderId, atLevel + better);
    }
    return map;
  }, [openOrders, ordersByTicker, getOrderbookRaw, marketVersion]);

  // Fire one-shot alerts when an order's cumulative queue position crosses a
  // threshold from above. Tracking the previously-seen value per orderId means
  // a freshly observed order already deep in the queue (e.g. on page load)
  // doesn't spuriously alert — only an actual downward crossing does.
  const prevQueueRef = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    const prev = prevQueueRef.current;
    const next = new Map<string, number>();
    let warn = false;
    let crit = false;
    for (const [orderId, q] of cumulativeQueueByOrderId) {
      if (q == null) continue;
      next.set(orderId, q);
      const last = prev.get(orderId);
      if (last == null) continue;
      if (last > QUEUE_CRITICAL_THRESHOLD && q <= QUEUE_CRITICAL_THRESHOLD) {
        crit = true;
      } else if (last > QUEUE_WARNING_THRESHOLD && q <= QUEUE_WARNING_THRESHOLD) {
        warn = true;
      }
    }
    prevQueueRef.current = next;
    if (crit || warn) {
      const settings = readSoundSettings();
      if (settings.queueFrontSoundEnabled) {
        if (crit) playQueueCriticalTone();
        else if (warn) playQueueWarningTone();
      }
    }
  }, [cumulativeQueueByOrderId]);

  const [closingTicker, setClosingTicker] = useState<string | null>(null);
  const [cancelingOrder, setCancelingOrder] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const handleClosePosition = useCallback(
    async (ticker: string, side: 'yes' | 'no', qty: number) => {
      setClosingTicker(ticker);
      try {
        await placeOrder({
          ticker,
          side,
          action: 'sell',
          type: 'market',
          count: qty,
        });
      } catch (err) {
        console.error('Close position failed:', err);
      } finally {
        setClosingTicker(null);
      }
    },
    [placeOrder],
  );

  const handleCancelOrder = useCallback(
    async (orderId: string) => {
      setCancelingOrder(orderId);
      try {
        await cancelOrder(orderId);
      } catch (err) {
        console.error('Cancel order failed:', err);
      } finally {
        setCancelingOrder(null);
      }
    },
    [cancelOrder],
  );

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([refreshPositions(), refreshOrders(), refreshPnl()]);
    setRefreshing(false);
  }, [refreshPositions, refreshOrders, refreshPnl]);

  if (status !== 'connected') {
    return (
      <div className="text-muted-foreground flex h-full flex-col items-center justify-center gap-3">
        <Crosshair size={48} strokeWidth={1} />
        <h2 className="text-foreground text-lg font-medium">Position Tracker</h2>
        <p className="text-sm">Connect your Kalshi account in Settings to track positions</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-6">
      <div className="mx-auto flex max-w-7xl gap-6">
        <div className="min-w-0 flex-1 space-y-5">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Crosshair size={20} className="text-muted-foreground" />
              <h1 className="text-xl font-semibold">Positions</h1>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1.5 transition-colors"
              >
                <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              </button>
              {balance !== null ? (
                <span className="text-muted-foreground text-sm">
                  Balance:{' '}
                  <span className="text-foreground font-medium">
                    ${(balance / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </span>
              ) : keyMode === 'org' ? (
                <span className="text-muted-foreground text-xs">Balance tracked at org level</span>
              ) : null}
            </div>
          </div>

          {/* Summary Cards */}
          <div className={`grid gap-3 ${keyMode === 'org' ? 'grid-cols-4' : 'grid-cols-3'}`}>
            <div className="bg-card border-border rounded-lg border px-4 py-3">
              <div className="text-muted-foreground text-xs">Unrealized P&L</div>
              <div
                className={`mt-1 text-lg font-semibold ${
                  totalPnl >= 0 ? 'text-green-400' : 'text-red-400'
                }`}
              >
                {formatDollars(totalPnl)}
              </div>
            </div>
            {keyMode === 'org' && (
              <div className="bg-card border-border rounded-lg border px-4 py-3">
                <div className="text-muted-foreground text-xs">Realized P&L</div>
                <div
                  className={`mt-1 text-lg font-semibold ${
                    realizedPnlCents >= 0 ? 'text-green-400' : 'text-red-400'
                  }`}
                >
                  {formatDollars(realizedPnlCents)}
                </div>
              </div>
            )}
            <div className="bg-card border-border rounded-lg border px-4 py-3">
              <div className="text-muted-foreground text-xs">Positions</div>
              <div className="text-foreground mt-1 text-lg font-semibold">
                {positions.length}
                {(() => {
                  // Mark-to-market dollar value across all open positions.
                  // Falls back to averagePrice when Hydra hasn't streamed a
                  // current mark yet so the figure stays populated.
                  const valueDollars = positionsWithPnl.reduce((sum, p) => {
                    const priceCents = p.currentPrice ?? p.averagePrice;
                    return sum + (priceCents * Math.abs(p.position)) / 100;
                  }, 0);
                  return (
                    <span className="text-muted-foreground ml-1 text-sm font-normal">
                      / ${valueDollars.toFixed(2)}
                    </span>
                  );
                })()}
              </div>
            </div>
            <div className="bg-card border-border rounded-lg border px-4 py-3">
              <div className="text-muted-foreground text-xs">Open Orders</div>
              <div className="text-foreground mt-1 text-lg font-semibold">{openOrders.length}</div>
            </div>
          </div>

          {/* Positions Table */}
          <div className="bg-card border-border rounded-lg border">
            <div className="border-border border-b px-4 py-2.5">
              <h2 className="text-foreground text-sm font-medium">Open Positions</h2>
            </div>
            {positionsWithPnl.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                    <th className="px-4 py-2 text-left">Market</th>
                    <th className="px-3 py-2 text-center">Side</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Avg</th>
                    <th className="px-3 py-2 text-right">Current</th>
                    <th className="px-3 py-2 text-right">P&L</th>
                    <th className="w-10 px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {positionsWithPnl.map((pos) => {
                    const { marketLabel, betTeam } = describeMarket(pos.ticker, pos.side);
                    return (
                    <tr
                      key={`${pos.ticker}-${pos.side}`}
                      className="border-border/40 hover:bg-muted/30 border-b"
                      title={pos.ticker}
                    >
                      <td className="text-foreground px-4 py-2 font-mono text-xs">
                        {marketLabel}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                            pos.side === 'yes'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}
                        >
                          {betTeam ? `(${betTeam}) ` : ''}{pos.side.toUpperCase()}
                        </span>
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {pos.position}
                      </td>
                      <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                        {formatCents(pos.averagePrice)}
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {pos.currentPrice !== null ? formatCents(pos.currentPrice) : '—'}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono text-xs font-medium ${
                          pos.unrealizedPnl === null
                            ? 'text-muted-foreground'
                            : pos.unrealizedPnl >= 0
                              ? 'text-green-400'
                              : 'text-red-400'
                        }`}
                      >
                        {pos.unrealizedPnl !== null ? formatDollars(pos.unrealizedPnl) : '—'}
                      </td>
                      <td className="px-2 py-2">
                        <button
                          onClick={() => handleClosePosition(pos.ticker, pos.side, pos.position)}
                          disabled={closingTicker === pos.ticker}
                          className="text-muted-foreground rounded p-1 transition-colors hover:bg-red-500/10 hover:text-red-400 disabled:opacity-50"
                          title="Close position (market sell)"
                        >
                          {closingTicker === pos.ticker ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            <X size={12} />
                          )}
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="text-muted-foreground px-4 py-8 text-center text-sm">
                No open positions
              </div>
            )}
          </div>

          {/* Open Orders Table */}
          <div className="bg-card border-border rounded-lg border">
            <div className="border-border border-b px-4 py-2.5">
              <h2 className="text-foreground text-sm font-medium">Open Orders</h2>
            </div>
            {openOrders.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                    <th className="px-4 py-2 text-left">Market</th>
                    <th className="px-3 py-2 text-center">Side</th>
                    <th className="px-3 py-2 text-center">Type</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Bid</th>
                    <th className="px-3 py-2 text-right">Last</th>
                    <th className="px-3 py-2 text-right">Queue</th>
                    <th className="px-3 py-2 text-center">Status</th>
                    <th className="w-10 px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {openOrders.map((order) => {
                    const { marketLabel, betTeam } = describeMarket(order.ticker, order.side);
                    // Kalshi often returns only yes_price even for NO orders; for
                    // a NO bid the actual price the user placed is 100 - yes_price.
                    const bidCents =
                      order.side === 'yes'
                        ? (order.yesPrice ?? (order.noPrice != null ? 100 - order.noPrice : null))
                        : (order.noPrice ?? (order.yesPrice != null ? 100 - order.yesPrice : null));
                    // Last trade comes from hydra (YES-cents); invert for NO orders.
                    const lastYes = getLastTradeYesCents(order.ticker);
                    const lastSide =
                      lastYes == null ? null : order.side === 'yes' ? lastYes : 100 - lastYes;
                    const queue = cumulativeQueueByOrderId.get(order.orderId) ?? null;
                    const queueTint =
                      queue == null
                        ? ''
                        : queue <= QUEUE_CRITICAL_THRESHOLD
                          ? 'bg-red-500/15'
                          : queue <= QUEUE_WARNING_THRESHOLD
                            ? 'bg-yellow-500/10'
                            : '';
                    return (
                    <tr
                      key={order.orderId}
                      className={`border-border/40 hover:bg-muted/30 border-b ${queueTint}`}
                      title={order.ticker}
                    >
                      <td className="text-foreground px-4 py-2 font-mono text-xs">
                        {marketLabel}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                            order.side === 'yes'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}
                        >
                          {betTeam ? `(${betTeam}) ` : ''}{order.side.toUpperCase()}
                        </span>
                      </td>
                      <td className="text-muted-foreground px-3 py-2 text-center text-xs capitalize">
                        {order.type}
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {order.remainingCount}
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {bidCents != null ? formatCents(bidCents) : '—'}
                      </td>
                      <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                        {lastSide != null ? formatCents(Math.round(lastSide)) : '—'}
                      </td>
                      <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                        {queue == null
                          ? '—'
                          : queue < 1
                            ? 'Front'
                            : Math.round(queue).toLocaleString()}
                      </td>
                      <td className="text-muted-foreground px-3 py-2 text-center text-xs capitalize">
                        {order.status}
                      </td>
                      <td className="px-2 py-2">
                        <button
                          onClick={() => handleCancelOrder(order.orderId)}
                          disabled={cancelingOrder === order.orderId}
                          className="text-muted-foreground rounded p-1 transition-colors hover:bg-red-500/10 hover:text-red-400 disabled:opacity-50"
                          title="Cancel order"
                        >
                          {cancelingOrder === order.orderId ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            <X size={12} />
                          )}
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="text-muted-foreground px-4 py-8 text-center text-sm">
                No open orders
              </div>
            )}
          </div>
        </div>

        {/* Activity Ticker sidebar */}
        <div className="hidden w-72 shrink-0 lg:block">
          <div className="sticky top-6">
            <div className="bg-card border-border rounded-lg border">
              <div className="border-border flex items-center gap-2 border-b px-4 py-2.5">
                <Activity size={14} className="text-muted-foreground" />
                <h2 className="text-foreground text-sm font-medium">Recent Fills</h2>
              </div>
              <div className="max-h-[calc(100vh-12rem)] overflow-y-auto py-1">
                <ActivityTicker />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Manager view: own data + per-trader summary ─────────────────

function ManagerPositionsView({
  currentUserId,
  onSelectTrader,
}: {
  currentUserId: string | null;
  onSelectTrader?: (row: OrgTraderRow) => void;
}) {
  return (
    <div className="h-full overflow-auto">
      <div className="mx-auto max-w-7xl px-6 pb-6">
        <TraderView />
        <OrgTradersTable
          highlightUserId={currentUserId ?? undefined}
          onRowClick={onSelectTrader}
          title="My Traders"
        />
      </div>
    </div>
  );
}

// ── Admin view: per-org summary ─────────────────────────────────

interface OrgSummaryRow {
  orgId: string;
  name: string;
  hasKeys: boolean;
  openCount: number;
  openNotionalCents: number;
  openOrderCount: number;
  realizedPnlCents: number;
}

function AdminPositionsView({
  onSelectOrg,
}: {
  onSelectOrg?: (row: OrgSummaryRow) => void;
}) {
  const [rows] = useState<OrgSummaryRow[]>([]);
  const [loading] = useState(false);
  const [error] = useState<string | null>(null);
  const fetchRows = () => {};

  return (
    <div className="h-full overflow-auto p-6">
      <div className="mx-auto max-w-7xl space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 size={20} className="text-muted-foreground" />
            <h1 className="text-xl font-semibold">Organizations</h1>
            <span className="text-muted-foreground text-xs">
              click an org to view its activity
            </span>
          </div>
          <button
            onClick={fetchRows}
            disabled={loading}
            className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1.5 transition-colors"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {error && (
          <div className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</div>
        )}

        <div className="bg-card border-border rounded-lg border">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
            </div>
          ) : rows.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center text-sm">No organizations.</p>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                  <th className="px-4 py-2 text-left">Organization</th>
                  <th className="px-3 py-2 text-right">Open Positions</th>
                  <th className="px-3 py-2 text-right">Open Notional</th>
                  <th className="px-3 py-2 text-right">Open Orders</th>
                  <th className="px-3 py-2 text-right">Realized P&L</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const clickable = !!onSelectOrg;
                  return (
                    <tr
                      key={r.orgId}
                      onClick={clickable ? () => onSelectOrg(r) : undefined}
                      className={`border-border/40 border-b ${
                        clickable ? 'hover:bg-muted/30 cursor-pointer' : ''
                      }`}
                    >
                      <td className="text-foreground px-4 py-2">
                        {r.name}
                        {!r.hasKeys && (
                          <span className="text-muted-foreground ml-2 text-[10px]">
                            (no key)
                          </span>
                        )}
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {r.openCount}
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        ${(r.openNotionalCents / 100).toFixed(2)}
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {r.openOrderCount}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono text-xs font-medium ${
                          r.realizedPnlCents >= 0 ? 'text-green-500' : 'text-red-500'
                        }`}
                      >
                        {formatDollarsSigned(r.realizedPnlCents)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export type { OrgSummaryRow };
